import { Alert, Button, Form, Input, InputNumber, Modal, Space, Switch, notification } from 'antd';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import { Department, DepartmentFormValues, isDepartmentActive } from 'types/department';
import AxiosUtils from 'utils/axios-utils';
import { getDepartmentError } from './department-error';

interface Props {
  open: boolean;
  department: Department | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DepartmentFormModal = ({ open, department, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm<DepartmentFormValues>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      code: department?.code ?? '',
      name: department?.name ?? '',
      description: department?.description ?? '',
      isActive: department ? isDepartmentActive(department) : true,
      sortOrder: department?.sortOrder ?? 0,
    });
  }, [department, form, open]);

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: DepartmentFormValues) => {
    const payload = {
      code: values.code.trim().toUpperCase(),
      name: values.name.trim(),
      description: values.description?.trim() ?? '',
      isActive: values.isActive,
      sortOrder: values.sortOrder,
    };

    setSaving(true);
    try {
      if (department) {
        await AxiosUtils.patch(`${API.departments}/${department.id}`, payload);
        notification.success({ message: 'Đã cập nhật bộ phận.' });
      } else {
        await AxiosUtils.post(API.departments, payload);
        notification.success({ message: 'Đã tạo bộ phận.' });
      }
      onSuccess();
      form.resetFields();
      onClose();
    } catch (error) {
      notification.error({ message: getDepartmentError(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={department ? 'Sửa bộ phận' : 'Thêm bộ phận'}
      open={open}
      onCancel={handleClose}
      footer={null}
      maskClosable={false}
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        {department && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Đổi mã sẽ cập nhật bộ phận của user, nhưng không cập nhật mã đã lưu ở ca làm."
          />
        )}
        {!department && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="API user hiện chỉ nhận 9 mã mặc định; mã mới cần được backend hỗ trợ trước khi gán cho user."
          />
        )}
        <Form.Item
          label="Mã bộ phận"
          name="code"
          extra="Mã phải duy nhất, kể cả với bộ phận đã xoá."
          normalize={(value) => (typeof value === 'string' ? value.toUpperCase().trim() : value)}
          rules={[
            { required: true, message: 'Vui lòng nhập mã bộ phận.' },
            { max: 64, message: 'Mã tối đa 64 ký tự.' },
            { pattern: /^[A-Z][A-Z0-9_]*$/, message: 'Bắt đầu bằng chữ in hoa; chỉ dùng A–Z, 0–9 và _.' },
          ]}
        >
          <Input maxLength={64} placeholder="VD: OPERATIONS" autoComplete="off" />
        </Form.Item>
        <Form.Item
          label="Tên bộ phận"
          name="name"
          rules={[
            { required: true, whitespace: true, message: 'Vui lòng nhập tên bộ phận.' },
            { max: 120, message: 'Tên tối đa 120 ký tự.' },
          ]}
        >
          <Input maxLength={120} showCount placeholder="VD: Vận hành" />
        </Form.Item>
        <Form.Item
          label="Mô tả"
          name="description"
          rules={[{ max: 500, message: 'Mô tả tối đa 500 ký tự.' }]}
        >
          <Input.TextArea rows={3} maxLength={500} showCount placeholder="Mô tả bộ phận" />
        </Form.Item>
        <Form.Item
          label="Thứ tự hiển thị"
          name="sortOrder"
          rules={[{ required: true, message: 'Vui lòng nhập thứ tự hiển thị.' }]}
        >
          <InputNumber min={0} precision={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Trạng thái" name="isActive" valuePropName="checked">
          <Switch checkedChildren="Đang bật" unCheckedChildren="Đã tắt" />
        </Form.Item>
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} disabled={saving}>Huỷ</Button>
          <Button type="primary" htmlType="submit" loading={saving}>
            {department ? 'Lưu thay đổi' : 'Tạo bộ phận'}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default DepartmentFormModal;
